import { get, set } from 'lodash';
import { type Dispatch, type SetStateAction, useCallback, useRef } from 'react';
import { isUnsetValue } from '../list/createDefaultsFromViewContext';

type CreateDefaultOffer = { prop: string; value: unknown };

/**
 * Queues create-only prefills from FormWidgets and applies them in one setState after mount,
 * so multiple fields cannot overwrite each other with stale state.
 */
export function useRegisterCreateDefault(
    setState?: Dispatch<SetStateAction<Record<string, unknown>>>
): (prop: string, value: unknown) => void {
    const pendingCreateDefaults = useRef<CreateDefaultOffer[]>([]);

    return useCallback(
        (prop: string, value: unknown) => {
            if (!setState) {
                return;
            }

            pendingCreateDefaults.current.push({ prop, value });
            if (pendingCreateDefaults.current.length > 1) {
                return;
            }

            // Wait for every field to finish mounting, then apply once. A later tick avoids dropdown
            // widgets clearing values they initialized with during the same mount cycle.
            setTimeout(() => {
                const offers = pendingCreateDefaults.current;
                pendingCreateDefaults.current = [];

                setState((current) => {
                    const newState = { ...current };
                    offers.forEach((offer) => {
                        if (isUnsetValue(get(newState, offer.prop))) {
                            set(newState, offer.prop, offer.value);
                        }
                    });
                    return newState;
                });
            }, 0);
        },
        [setState]
    );
}
